import { useEffect, useState } from 'react';
import {
    Box, AppBar, Toolbar, IconButton, Typography, Button, List,
    ListItem, ListItemButton, ListItemAvatar, Avatar, ListItemText,
    Checkbox, Paper, TextField, InputAdornment, Divider,
    CircularProgress
} from '@mui/material';
import { getConnections } from "../../services/UserProfileApi";

import { ArrowBack, Search, GroupAdd, CheckCircle, Groups, VerifiedUser } from '@mui/icons-material';
import InfiniteScroll from 'react-infinite-scroll-component';
import MentorBadge from '../../components/MentorBadge';

const ConnectionList = ({ user, onBack, onStartChat, getImageUrlWithToken }) => {
    const [mode, setMode] = useState('list');
    const [selectedIds, setSelectedIds] = useState([]);
    const [groupName, setGroupName] = useState('');
    const [searchTerm, setSearchTerm] = useState(''); // State for Search

    const handleToggleSelect = (id) => {
        const currentIndex = selectedIds.indexOf(id);
        const newChecked = [...selectedIds];
        if (currentIndex === -1) newChecked.push(id);
        else newChecked.splice(currentIndex, 1);
        setSelectedIds(newChecked);
    };

    const handleIndividualClick = (user) => {
        if (mode === 'group-select') {
            handleToggleSelect(user.id);
        } else {
            // CONSTRUCT THE CHAT OBJECT
            // This structure must match what 'selectedChat' expects in Messages.js
            const chatObject = {
                chat_id: user.chat_id, // Ensure your API returns this, or use user.id if creating fresh
                user_id: user.id,      // Keep track of the target user ID
                chat_image: user.thumbnail_pic,
                chat_name: user.name,
                is_group: 0,           // Important: 0 for 1-on-1
                is_online: user.is_online || 0, // Default to offline if undefined
                is_mentor: user.is_mentor || 0  // If you use badges
            };
            onStartChat(chatObject);
        }
    };



    const handleCreateGroup = () => {
        const newGroup = {
            chat_id: null, // New chat, so no ID yet
            user_id: selectedIds, // Array of selected user IDs
            chat_image: null, // Could set a default group image
            chat_name: groupName,
            is_group: 1, // Important: 1 for group chats
            is_online: null,
        };
        onStartChat(newGroup);
    };

    const [connections, setConnections] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

  

    // Reset & load first page when user changes
    useEffect(() => {
        if (!user) return;

        setConnections([]);
        setPage(1);
        setHasMore(true);

        fetchConnections(1);
    }, [user, searchTerm]);

    const fetchConnections = (pageNo) => {
        getConnections(user?.username, pageNo, searchTerm)
            .then((data) => {
                console.log("Fetched connections from page", pageNo, ":", data);
                const newConnections = data.connections || [];

                // 🔹 Append new data
                setConnections((prev) =>
                    pageNo === 1 ? newConnections : [...prev, ...newConnections]
                );

                // 🔹 Update pagination
                setPage(pageNo);

                // 🔹 Stop loading when last page reached
                if (data.status === false || pageNo >= data.last_page) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            })
            .catch((error) => {
                console.error("Error fetching connections:", error);
                setHasMore(false);
            });
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* HEADER */}
            <AppBar position="static" elevation={0} color="default">
                <Toolbar sx={{ px: 2, bgcolor: 'background.paper', display: 'flex', gap: 1 }} disableGutters>
                    <IconButton edge="start" onClick={mode === 'list' ? onBack : () => setMode('list')}>
                        <ArrowBack />
                    </IconButton>

                    <Box sx={{ flexGrow: 1, ml: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                            {mode === 'list' && "New Chat"}
                            {mode === 'group-select' && "Add Participants"}
                            {mode === 'group-details' && "Group Info"}
                        </Typography>
                    </Box>

                    {/* Header Actions */}
                    {mode === 'group-select' && selectedIds.length > 1 && (
                        <Button variant='contained' disableElevation size='small' onClick={() => setMode('group-details')} sx={{ textTransform: "none" }}>Next</Button>
                    )}
                    {mode === 'group-details' && (
                        <Button variant='contained' disableElevation size='small' disabled={!groupName} onClick={handleCreateGroup} sx={{ textTransform: "none" }}>Create</Button>
                    )}
                </Toolbar>
            </AppBar>

            {/* SEARCH BAR (Only visible in 'list' or 'group-select' modes) */}
            {mode !== 'group-details' && (
                <Box sx={{ p: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search people..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 5 }
                        }}
                    />
                </Box>
            )}

            {/* CONTENT AREA */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }} id="scrollableDiv">

                {/* 1. VIEW: LIST & SELECTION */}
                {(mode === 'list' || mode === 'group-select') && (
                    <List sx={{ py: 0 }}>

                        {/* "Create Group" Button - Hide if user is searching */}
                        {mode === 'list' && !searchTerm && (
                            <>
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => setMode('group-select')} sx={{ py: 2 }}>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: "background.default" }}>
                                                <GroupAdd color='primary' />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText primary="Create New Group" />
                                    </ListItemButton>
                                </ListItem>
                                <Divider />
                            </>
                        )}

                        {/* List Header */}
                        <Box sx={{ px: 2, py: 1, bgcolor: 'background.default' }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                {mode === 'group-select' ? 'SELECT PARTICIPANTS' : user.account_type === 'organization' ? 'FOLLOWERS' : 'CONNECTIONS'}
                            </Typography>
                        </Box>

                        <InfiniteScroll
                            dataLength={connections.length}
                            next={() => fetchConnections(page + 1)}
                            hasMore={hasMore}
                            loader={<Box sx={{ textAlign: "center", py: 1 }}><CircularProgress size={20} /></Box>}
                            scrollableTarget="scrollableDiv"
                            endMessage={
                                <Box sx={{ textAlign: "center", py: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        No more {user.account_type === 'organization' ? 'followers' : 'connections'} to load.
                                    </Typography>
                                </Box>
                            }
                        >
                            {connections.map((user) => {
                                const isSelected = selectedIds.indexOf(user.id) !== -1;
                                return (
                                    <ListItem
                                        key={user.id}
                                        disablePadding
                                        secondaryAction={
                                            mode === 'group-select' && (
                                                <Checkbox
                                                    edge="end"
                                                    checked={isSelected}
                                                    onChange={() => handleToggleSelect(user.id)}
                                                    icon={<CheckCircle color="disabled" />}
                                                    checkedIcon={<CheckCircle color="success" />}
                                                />
                                            )
                                        }
                                    >
                                        <ListItemButton onClick={() => handleIndividualClick(user)}>
                                            <ListItemAvatar>
                                                {user.is_mentor ? (
                                                    <MentorBadge isSmall>
                                                        <Avatar src={getImageUrlWithToken(user.thumbnail_pic)} />
                                                    </MentorBadge>
                                                ) : (
                                                    <Avatar src={getImageUrlWithToken(user.thumbnail_pic)} />
                                                )}
                                            </ListItemAvatar>


                                            <ListItemText
                                                primary={
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        {user.name}
                                                        {user.is_verified && <VerifiedUser fontSize="small" color="success" sx={{ fontSize: 16, mb: 0.3 }} />}
                                                    </span>
                                                }
                                                secondary={user.describes_me}
                                                slotProps={{
                                                    primary: {
                                                        noWrap: true,
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                    },
                                                    secondary: {
                                                        noWrap: true,
                                                        fontSize: 12,
                                                    }
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </InfiniteScroll>
                    </List>
                )}

                {/* 2. VIEW: GROUP DETAILS */}
                {mode === 'group-details' && (
                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar sx={{ width: 100, height: 100, bgcolor: 'background.default' }}>
                                <Groups color='primary' sx={{ fontSize: 40 }} />
                            </Avatar>
                        </Box>

                        <Paper elevation={0} sx={{ width: '100%', p: 2, borderRadius: 2 }}>
                            <TextField
                                autoFocus
                                fullWidth
                                label="Group Name"
                                variant="standard"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                placeholder="Type group name here..."
                            />
                        </Paper>

                        <Typography variant="body2" color="text.secondary">
                            Participants: {selectedIds.length}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ConnectionList;